import { css } from 'lit';
import { buttonReset, buttonDisabled } from '@styles/index.js';
import { breakpoint } from '@styles/mixins/breakpoints.css.js';

export default css`
  /*
 Themable with these properties
  --toolbar-color-bg
  --toolbar-color-icon
  --toolbar-color-icon-hover
  --toolbar-color-icon-hover-bg
*/
  :host {
    background: var(--toolbar-color-bg, var(--vst-color-bg-secondary));
  }

  .toolbar {
    position: relative;
    display: flex;
    height: var(--vst-toolbar-height);
    padding: 0 var(--vst-space-xs);
  }

  /* //////////////////////////// */
  .left-content,
  .right-content {
    display: flex;
    flex: 1;
    align-items: center;
  }

  .left-content {
    min-inline-size: 0;
  }

  .right-content {
    justify-content: flex-end;
  }

  .center-content {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 var(--vst-space-xs);
  }

  :host(.show-wait) .toolbar-center-button {
    z-index: calc(var(--vst-z-popover) + 1);
  }

  .toolbar-center-icon {
    display: inline-flex;
    line-height: 0;
  }

  .toolbar-center-icon svg {
    block-size: var(--vst-icon-size-m);
    inline-size: var(--vst-icon-size-m);
  }

  .toolbar-center-label {
    display: none;
  }

  ${breakpoint(
    's',
    css`
      .toolbar-center-icon {
        display: none;
      }

      .toolbar-center-label {
        display: inline;
      }
    `,
  )}

  ${breakpoint(
    's',
    css`
      .toolbar-center-label.toolbar-center-two-line {
        align-items: center;
        display: inline-flex;
        flex-direction: column;
        font-size: var(--vst-font-size-xs);
        line-height: 1.15;
      }

      .toolbar-center-second-line {
        display: block;
      }
    `,
  )}

  ${buttonDisabled(css`.toolbar-center-button`)}
  ${buttonReset(css`.toolbar-center-button`)}
  .toolbar-center-button {
    align-items: center;
    background-color: var(--vst-color-btn-graph-bg);
    border-radius: var(--vst-radius);
    border: 1px solid var(--vst-color-btn-graph-border);
    box-shadow: var(--vst-shadow-xs);
    color: var(--vst-color-text-brand);
    display: flex;
    font-size: 0.875rem;
    font-weight: bold;
    height: 2.25rem;
    min-inline-size: auto;
    text-transform: uppercase;
    transition-duration: var(--transition-fastest-speed);
    transition: opacity var(--transition-default-speed) var(--transition-easing);
  }
  .toolbar-center-button[disabled] {
    cursor: default;
    opacity: 0.2;
    pointer-events: none;
  }
  .toolbar-center-button:hover,
  .toolbar-center-button:active {
    background: var(--vst-color-btn-graph-bg-hover);
    border: 1px solid var(--vst-color-btn-graph-border-hover);
    box-shadow: var(--vst-shadow-btn-hover);
    color: var(--vst-color-text-brand);
    cursor: pointer;
    outline: 0;
  }
  .toolbar-center-button:active {
    box-shadow: var(--vst-shadow-xs), var(--box-shadow-inset);
  }
  ${breakpoint(
    's',
    css`
      .toolbar-center-button {
        min-inline-size: 5.875rem;
      }
    `,
  )}

  .toolbar-center-button[variant='secondary'] {
    background: var(--vst-color-brand);
    border: 1px solid var(--vst-color-brand);
    color: #fff;
    margin-inline-start: var(--vst-space-xs);
  }
  .toolbar-center-button[variant='secondary']:hover,
  .toolbar-center-button[variant='secondary']:active {
    background: var(--vst-color-brand-dark);
    border: 1px solid var(--vst-color-brand);
    color: #fff;
  }
  .toolbar-center-button[variant='secondary'][disabled]:hover {
    background: var(--vst-color-brand);
    border: 1px solid var(--vst-color-brand);
    color: #fff;
  }

  ${buttonReset(css`
    ::slotted(.toolbar-icon);
  `)}

  ::slotted(.toolbar-icon) {
    position: relative;
    display: flex;
    align-items: center;
    height: 2.25rem;
    background-color: transparent;
    border-radius: var(--vst-radius);
    border: 1px solid transparent;
    padding: 0 0.3125rem;
    background-clip: padding-box;
    /*  https://stackoverflow.com/a/5612502 */
    font-size: var(--vst-font-size-xs);
    font-weight: bold;
    color: var(--toolbar-color-icon, var(--vst-color-fg-secondary));
    --vst-ui-icon-color: var(--toolbar-color-icon, var(--vst-color-fg-secondary));
  }

  ::slotted(.toolbar-icon:hover),
  ::slotted(.toolbar-icon:focus) {
    outline: 0;
    cursor: pointer;
    background-color: var(--toolbar-color-icon-hover-bg, var(--vst-color-bg));
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: var(--vst-shadow-xs);
    color: var(--toolbar-color-icon-hover, #000);
    --vst-ui-icon-color: var(--toolbar-color-icon-hover, var(--vst-color-fg-primary));
    --toolbar-color-icon-hover-bg: var(--vst-color-btn-toolbar-bg-hover);
  }

  ::slotted(.toolbar-icon:active) {
    background-color: var(--vst-color-bg);
    box-shadow: var(--box-shadow-inset);
  }

  ::slotted(.toolbar-icon:disabled) {
    opacity: 0.2;
    cursor: default;
    pointer-events: none;
  }

  ::slotted(.experiment-meta) {
    --inline-size: auto;
    display: flex;
    flex-basis: auto;
    flex-shrink: 1;
    align-items: center;
    inline-size: fit-content;
    max-inline-size: 100%;
  }
`;
